import React from 'react';
import {
  Badge,
  Box,
  ChevronDownIcon,
  ChevronUpIcon,
  EditIcon,
  EyeIcon,
  Heading,
  ListIcon,
  Text,
  TypeIcon,
} from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';
import { FIELD_TYPE_META, FIELD_TYPE_OPTIONS } from '../../../constants';
import {
  EmployeeFieldAccess,
  EmployeeFieldAccessRole,
  EmployeeFieldFormData,
  FieldPreviewState,
} from '../../../types';
import { PreviewPanel, ReviewAccessHeader, ReviewIconTile } from '../../../styles';
import { FieldPreview } from './FieldPreview';

export type ReviewStepProps = {
  roles: EmployeeFieldAccessRole[];
  resolvedAccessPolicy: Record<number, EmployeeFieldAccess>;
  isEditMode: boolean;
  isReviewAccessOpen: boolean;
  onToggleReviewAccess: () => void;
  preview: FieldPreviewState;
  onPreviewChange: (nextPreview: Partial<FieldPreviewState>) => void;
};

export const ReviewStep = ({
  roles,
  resolvedAccessPolicy,
  isEditMode,
  isReviewAccessOpen,
  onToggleReviewAccess,
  preview,
  onPreviewChange,
}: ReviewStepProps) => {
  const { formData } = useFormWithProvider<EmployeeFieldFormData>();
  const name = formData.name ?? '';
  const valueType = formData.valueType ?? '';
  const enumValues = formData.enumValues ?? [];
  const previewLabel = name || 'Name';

  const selectedType = FIELD_TYPE_OPTIONS.find((option) => option.value === valueType);
  const ReviewIcon = valueType ? FIELD_TYPE_META[valueType].Icon : TypeIcon;
  const editRoles = roles.filter((role) => resolvedAccessPolicy[role.roleId] === 'EDIT');
  const viewOnlyRoles = roles.filter((role) => resolvedAccessPolicy[role.roleId] === 'VIEW_ONLY');
  const hiddenRoles = roles.filter((role) => resolvedAccessPolicy[role.roleId] === 'HIDDEN');

  return (
    <Box padding={['spacing.8', 'spacing.7', 'spacing.6']} maxWidth="560px">
      <Heading size="large">Review</Heading>
      <Text marginTop="spacing.2" color="surface.text.gray.muted">
        Review the field before {isEditMode ? 'saving' : 'adding'} it to your workspace.
      </Text>
      <Box marginTop="spacing.5">
        <Box
          borderWidth="thin"
          borderColor="surface.border.gray.subtle"
          borderRadius="large"
          overflow="hidden"
          backgroundColor="surface.background.gray.intense">
          <Box
            display="grid"
            gridTemplateColumns="minmax(0, 1fr) minmax(260px, 300px)"
            gap="spacing.5"
            padding="spacing.5">
            <Box minWidth="0px">
              <ReviewIconTile>
                <ReviewIcon size="medium" color="interactive.icon.primary.normal" />
              </ReviewIconTile>
              <Heading size="medium" marginTop="spacing.4">
                {name || 'Name'}
              </Heading>
              <Text marginTop="spacing.2" color="interactive.text.primary.normal">
                {selectedType?.label ?? 'Text'}
              </Text>
              <Box marginTop="spacing.4" display="flex" gap="spacing.2" flexWrap="wrap">
                <Badge color="primary">Custom</Badge>
                {formData.isRequired ? <Badge color="notice">Mandatory</Badge> : null}
                {formData.showOnPayslip ? <Badge color="information">Payslip</Badge> : null}
              </Box>
              {valueType === 'ENUM' && enumValues.length > 0 ? (
                <Text marginTop="spacing.4" color="surface.text.gray.muted">
                  {enumValues.join(', ')}
                </Text>
              ) : null}
            </Box>
            <PreviewPanel>
              <Box display="flex" alignItems="center" gap="spacing.2" marginBottom="spacing.4">
                <ListIcon size="medium" color="surface.icon.gray.muted" />
                <Text weight="semibold">Preview</Text>
              </Box>
              <FieldPreview
                label={previewLabel}
                valueType={valueType}
                enumValues={enumValues}
                preview={preview}
                onPreviewChange={onPreviewChange}
              />
            </PreviewPanel>
          </Box>
          <ReviewAccessHeader
            type="button"
            aria-expanded={isReviewAccessOpen}
            onClick={onToggleReviewAccess}>
            <Text weight="semibold">Access</Text>
            <Box as="span" display="flex" alignItems="center" gap="spacing.5">
              <Box as="span" display="inline-flex" alignItems="center" gap="spacing.2">
                <EditIcon size="medium" color="interactive.icon.gray.normal" />
                <Text>Can edit • {editRoles.length}</Text>
              </Box>
              <Box as="span" display="inline-flex" alignItems="center" gap="spacing.2">
                <EyeIcon size="medium" color="interactive.icon.gray.normal" />
                <Text>View only • {viewOnlyRoles.length}</Text>
              </Box>
              {isReviewAccessOpen ? (
                <ChevronUpIcon size="medium" color="surface.icon.gray.muted" />
              ) : (
                <ChevronDownIcon size="medium" color="surface.icon.gray.muted" />
              )}
            </Box>
          </ReviewAccessHeader>
          {isReviewAccessOpen ? (
            <Box
              display="grid"
              gridTemplateColumns="144px minmax(0, 1fr)"
              rowGap="spacing.3"
              columnGap="spacing.5"
              borderTopWidth="thin"
              borderTopColor="surface.border.gray.subtle"
              padding={['spacing.4', 'spacing.5']}>
              <Text weight="semibold">Can edit</Text>
              <Text>{editRoles.map((role) => role.name).join(', ') || '-'}</Text>
              <Text weight="semibold">View only</Text>
              <Text>{viewOnlyRoles.map((role) => role.name).join(', ') || '-'}</Text>
              <Text weight="semibold">Hidden</Text>
              <Text>{hiddenRoles.map((role) => role.name).join(', ') || '-'}</Text>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};
